import { SITE_URL } from "@/components/Seo";

export const OCC_BASE = "/mini-pelles-occasion";
export const occPath = (o) => `${OCC_BASE}/${o.slug}`;
export const occTitle = (o) => `${o.marque} ${o.modele}`;
export const fmtEur = (n) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(n || 0);
export const fmtInt = (n) => new Intl.NumberFormat("fr-FR").format(n || 0);

export const STATUT = {
  disponible: { label: "Disponible", cls: "bg-[#16A34A] text-white", schema: "https://schema.org/InStock" },
  reserve: { label: "Réservée", cls: "bg-[#EA580C] text-white", schema: "https://schema.org/LimitedAvailability" },
  vendu: { label: "Vendue", cls: "bg-[#072135] text-[#D8B86A]", schema: "https://schema.org/SoldOut" },
};

export const CHENILLES = { Neuve: 100, "90 %": 90, "75 %": 75, "50 %": 50, "25 %": 25, "À remplacer": 5 };
export const ENTRETIEN_ITEMS = [["vidange_moteur", "Vidange moteur"], ["filtres", "Filtres (huile, air, gasoil)"], ["huile_hydraulique", "Huile hydraulique"], ["graissage", "Graissage complet"]];

export const poidsLabel = (kg) => (kg ? (kg >= 1000 ? `${(kg / 1000).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} T` : `${kg} kg`) : null);

export function occSpecs(o) {
  return [
    ["Marque", o.marque],
    ["Modèle", o.modele],
    ["Version", o.version],
    ["Année", o.annee],
    ["Heures", o.heures != null && o.heures !== "" ? `${fmtInt(o.heures)} h` : null],
    ["Poids", poidsLabel(o.poids_kg)],
    ["Moteur", o.moteur],
    ["État", o.etat],
    ["Visible à", o.localisation],
  ].filter(([, v]) => v);
}

export function occSeoTitle(o) {
  const bits = [`Mini-pelle d'occasion ${occTitle(o)}`, o.annee, o.heures != null ? `${fmtInt(o.heures)} h` : null].filter(Boolean);
  return o.statut === "vendu" ? `${bits.join(" · ")} — Vendue` : `${bits.join(" · ")} — ${fmtEur(o.prix)} €`;
}

export function occSeoDescription(o) {
  const specs = [o.version, o.annee && `année ${o.annee}`, o.heures != null && `${fmtInt(o.heures)} heures`, poidsLabel(o.poids_kg), o.usure_chenilles && `chenilles ${o.usure_chenilles === "Neuve" ? "neuves" : o.usure_chenilles}`, o.godets?.length && `${o.godets.length} godet${o.godets.length > 1 ? "s" : ""}`].filter(Boolean).join(", ");
  if (o.statut === "vendu") return `Mini-pelle d'occasion ${occTitle(o)} (${specs}) vendue par LMB Services à Bourgoin-Jallieu. Découvrez nos autres mini-pelles d'occasion disponibles.`;
  return `Mini-pelle d'occasion ${occTitle(o)} à vendre : ${specs}. ${fmtEur(o.prix)} € ${o.prix_mention}. Visible à ${o.localisation || "Bourgoin-Jallieu (38)"}, livraison possible partout en France.`;
}

export function occJsonLd(o) {
  const url = `${SITE_URL}${occPath(o)}`;
  return [
    {
      "@context": "https://schema.org",
      "@type": "Product",
      name: `Mini-pelle d'occasion ${occTitle(o)}${o.annee ? ` ${o.annee}` : ""}`,
      description: o.description || occSeoDescription(o),
      image: o.photos?.length ? o.photos : undefined,
      brand: { "@type": "Brand", name: o.marque },
      model: o.modele,
      category: "Mini-pelle d'occasion",
      ...(o.annee ? { productionDate: String(o.annee) } : {}),
      additionalProperty: [
        o.heures != null && { "@type": "PropertyValue", name: "Heures d'utilisation", value: o.heures, unitText: "h" },
        o.poids_kg && { "@type": "PropertyValue", name: "Poids", value: o.poids_kg, unitCode: "KGM" },
        o.version && { "@type": "PropertyValue", name: "Version", value: o.version },
        o.usure_chenilles && { "@type": "PropertyValue", name: "État des chenilles", value: o.usure_chenilles },
        o.godets?.length && { "@type": "PropertyValue", name: "Godets fournis", value: o.godets.join(", ") },
      ].filter(Boolean),
      offers: {
        "@type": "Offer",
        url,
        priceCurrency: "EUR",
        price: o.prix,
        itemCondition: "https://schema.org/UsedCondition",
        availability: STATUT[o.statut]?.schema,
        seller: { "@type": "Organization", name: "LMB Services — La Minipelle Berjalienne" },
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Accueil", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Mini-pelles d'occasion", item: `${SITE_URL}${OCC_BASE}` },
        { "@type": "ListItem", position: 3, name: occTitle(o), item: url },
      ],
    },
  ];
}

export const OCC_FAQ = [
  ["Combien coûte une mini-pelle d'occasion ?", "Le prix d'une mini-pelle d'occasion dépend surtout de son poids, de son année, de ses heures et de son état. Une mini-pelle de 1 tonne d'occasion se négocie généralement entre 5 000 et 12 000 €, une 1,5 à 2 tonnes entre 10 000 et 20 000 €, et une 3 tonnes à partir de 18 000 €. Chaque annonce indique le prix ferme, TTC ou HT."],
  ["Combien d'heures pour une mini-pelle d'occasion ?", "Sur une mini-pelle, moins de 1 500 heures est considéré comme peu utilisé, 1 500 à 4 000 heures comme un usage normal et au-delà de 5 000 heures comme un usage important. L'entretien régulier (vidanges, graissage, état des chenilles et des vérins) compte autant que le compteur."],
  ["Que vérifier avant d'acheter une mini-pelle d'occasion ?", "Contrôlez les chenilles, barbotins et galets, l'absence de fuite sur les vérins et flexibles, le jeu dans les axes de flèche et de balancier, le démarrage à froid du moteur, la fumée à l'échappement, la vitesse des mouvements hydrauliques et le carnet d'entretien. Nous vous montrons chaque machine en fonctionnement sur rendez-vous."],
  ["Mini-pelle d'occasion ou neuve : que choisir ?", "Une occasion permet d'acheter moins cher une machine déjà rodée. Une mini-pelle RIPPA neuve coûte souvent le prix d'une occasion de marque européenne, avec garantie et pièces neuves. Nous proposons les deux pour que vous puissiez comparer."],
  ["Livrez-vous les mini-pelles d'occasion ?", "Oui. Les machines sont visibles à Bourgoin-Jallieu (Isère), à 40 minutes de Lyon et 1 heure de Grenoble, et nous organisons la livraison partout en France sur devis."],
  ["Reprenez-vous ma mini-pelle ?", "Contactez-nous avec la marque, le modèle, l'année, les heures et quelques photos : nous étudions chaque proposition de reprise ou de rachat."],
];

export function occListJsonLd(items) {
  return [
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Mini-pelles d'occasion à vendre",
      itemListElement: items.map((o, i) => ({ "@type": "ListItem", position: i + 1, url: `${SITE_URL}${occPath(o)}`, name: `Mini-pelle d'occasion ${occTitle(o)}` })),
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: OCC_FAQ.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Accueil", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Mini-pelles d'occasion", item: `${SITE_URL}${OCC_BASE}` },
      ],
    },
  ];
}
